import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import client from "@/lib/api";
import { RiskBadge, PrimaryBadge } from "@/components/StatusBadge";
import { timeAgo, fmtDate, closeBlockShort, BOARD_FILTERS, BOARD_FILTER_LABELS, primaryStatus, RISK_STYLES } from "@/lib/format";
import { Plus, MapPin, X, TriangleAlert, CheckCircle2, ChevronDown, Ship } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const DEST_TYPES = ["Hotel / Resort", "Residential", "Commercial", "Warehouse", "Retail Store", "Other"];

const STAGE_META = {
  "New Job": "#64748B",
  "Pickup Scheduled": "#3F566E",
  "Picked Up": "#3F566E",
  "In Transit": "#0B132B",
  "At Facility": "#0B132B",
  "Ready for Delivery": "#3F566E",
  "Delivered": "#3F566E",
  "Ready to Close": "#059669",
  "Closed": "#64748B",
};

function JobCard({ job, stages, onMove, onOpen }) {
  const openIfKey = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(job); }
  };
  const status = primaryStatus(job);
  const RED = ["Missing Documents", "POD Missing", "Closeout Blocked"];
  const AMBER = ["Delayed", "Needs Attention", "Customer Update Due"];
  const tone = RED.includes(status) ? "red" : AMBER.includes(status) ? "amber" : "green";
  const toneWrap = { red: "bg-[#FFF5F5] border-l-4 border-l-[#DC2626]", amber: "bg-[#FFFDF9] border-l-4 border-l-[#D97706]", green: "bg-white border-l-4 border-l-[#059669]" }[tone];
  const toneBadge = { red: "bg-[#DC2626]", amber: "bg-[#D97706]", green: "bg-[#059669]" }[tone];
  const statusLabel = (RISK_STYLES[status] || {}).label || status;
  if (job.current_stage === "Closed") {
    return (
      <div
        data-testid={`board-card-${job.job_number}`}
        role="button"
        tabIndex={0}
        onClick={() => onOpen(job)}
        onKeyDown={openIfKey}
        className="bg-surface-alt border border-line rounded-sm p-3 opacity-90 hover:opacity-100 cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-steel/40"
      >
        <div className="flex items-start justify-between gap-2">
          <span className="font-mono-data text-[12px] font-medium text-ink-2">{job.job_number}</span>
          <PrimaryBadge job={job} />
        </div>
        <div className="mt-1.5">
          <div className="text-[13px] font-medium text-ink-2 truncate">{job.customer_name}</div>
          <div className="flex items-center gap-1 text-[11.5px] text-ink-3 mt-1">
          <span className="truncate">{job.pickup_location.split(",")[0]} → {job.delivery_location.split(",")[0]}</span>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-line space-y-0.5">
          <div className="text-[11px] text-ink-3 font-mono-data">Delivered {fmtDate(job.delivery_due_date)}</div>
          <div className="text-[11px] text-ink-3 font-mono-data">Closed {fmtDate(job.closed_at)} · {(job.closed_by || "").split(" ")[0]}</div>
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid={`board-card-${job.job_number}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(job)}
      onKeyDown={openIfKey}
      className={`border border-slate-200 rounded-sm p-3 cursor-pointer shadow-[1px_1px_2px_rgba(11,19,43,0.08)] transition-shadow hover:shadow-industrialHover focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B132B] ${toneWrap}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono-data text-[12px] font-medium text-ink">{job.job_number}</span>
        <span data-testid={`board-badge-${job.job_number}`} className={`${toneBadge} text-white text-[9px] font-black tracking-widest uppercase px-2 py-0.5 rounded`}>{statusLabel}</span>
      </div>
      <div className="mt-1.5">
        <div className="text-[13.5px] font-medium text-ink truncate">{job.customer_name}</div>
        <div className="flex items-center gap-1 text-[11.5px] text-ink-2 mt-1">
          <span className="truncate">{job.pickup_location.split(",")[0]} → {job.delivery_location.split(",")[0]}</span>
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-line">
        <p className="text-[11.5px] text-ink-2 leading-snug line-clamp-2">{job.next_action || "—"}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-[11px] text-ink-3">{job.assigned_owner.split(" ")[0]} · {timeAgo(job.last_updated_at)}</span>
        </div>
      </div>
      {job.current_stage === "Ready to Close" && (
        job.can_close ? (
          <div data-testid={`board-ready-${job.job_number}`} className="mt-2 flex items-center gap-1 text-[11px] text-ok font-medium">
            Ready for final close
          </div>
        ) : (
          <div data-testid={`board-blocked-${job.job_number}`} className="mt-2 flex items-start gap-1 text-[11px] text-danger">
            <span><span className="font-semibold">Closeout Blocked</span> · {closeBlockShort(job)}</span>
          </div>
        )
      )}
      <div className="mt-2" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <Select value={job.current_stage} onValueChange={(v) => onMove(job, v)}>
          <SelectTrigger data-testid={`board-stage-select-${job.job_number}`} className="h-7 text-[11.5px] border-line bg-surface-alt rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {stages.map((s) => (
              <SelectItem key={s} value={s} className="text-[12.5px]">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function NewJobDialog({ customers, users, stages, maritime, onCreated }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    customer_id: "", pickup_location: "", delivery_location: "",
    cargo_description: "", current_stage: "New Job", assigned_owner: user?.name || "",
    risk_status: "On Track", next_action: "",
    destination_property: "", destination_type: "", access_instructions: "",
    primary_contact_name: "", primary_contact_title: "", primary_contact_phone: "",
    backup_contact_name: "", backup_contact_title: "", backup_contact_phone: "",
    end_recipient_owner: "",
    vessel_name: "", departure_sailing_time: "", dock_terminal_slot: "",
  });
  const [showFinalMile, setShowFinalMile] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Default the owner to the current user; include them in options even if they
  // are not a workspace member (e.g. Super Admin testing the demo workspace).
  const ownerOptions = useMemo(() => {
    const set = new Set();
    if (user?.name) set.add(user.name);
    users.forEach((u) => u.name && set.add(u.name));
    return [...set].sort();
  }, [users, user]);

  // When the dialog opens with no owner chosen yet, default to the current user.
  useEffect(() => {
    if (open && !form.assigned_owner && user?.name) set("assigned_owner", user.name);
  }, [open, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.customer_id || !form.pickup_location || !form.delivery_location) {
      toast.error("Customer, pickup and delivery are required.");
      return;
    }
    try {
      const r = await client.post("/shipments", form);
      toast.success(`Created ${r.data.job_number}`);
      setOpen(false);
      setForm({ customer_id: "", pickup_location: "", delivery_location: "", cargo_description: "", current_stage: "New Job", assigned_owner: user?.name || "", risk_status: "On Track", next_action: "", destination_property: "", destination_type: "", access_instructions: "", primary_contact_name: "", primary_contact_title: "", primary_contact_phone: "", backup_contact_name: "", backup_contact_title: "", backup_contact_phone: "", end_recipient_owner: "", vessel_name: "", departure_sailing_time: "", dock_terminal_slot: "" });
      onCreated();
    } catch (e) {
      toast.error("Could not create job.");
    }
  };

  const field = "h-9 w-full px-3 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-shipment-btn" className="h-9 px-4 rounded-sm bg-brand text-white text-[13.5px] font-medium flex items-center gap-1.5 hover:bg-brand-dark transition-colors">
          <Plus className="h-4 w-4" /> New Shipment
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg rounded-md max-h-[90vh] !flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-heading">Create Shipment Job</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-2">Add a new shipment to the board. Job number is generated automatically.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex flex-col min-h-0 flex-1">
        <div className="space-y-3.5 px-6 py-3 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="op-label block mb-1.5">Customer</label>
            <Select value={form.customer_id} onValueChange={(v) => set("customer_id", v)}>
              <SelectTrigger data-testid="new-job-customer" className="h-9 border-line rounded-sm"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="op-label block mb-1.5">Pickup location</label>
              <input data-testid="new-job-pickup" className={field} value={form.pickup_location} onChange={(e) => set("pickup_location", e.target.value)} placeholder="Deep Water Harbour, AG" />
            </div>
            <div>
              <label className="op-label block mb-1.5">Delivery location</label>
              <input data-testid="new-job-delivery" enterKeyHint="next" inputMode="text" className={field} value={form.delivery_location} onChange={(e) => set("delivery_location", e.target.value)} placeholder="Jumby Bay Ferry Dock, AG" />
            </div>
          </div>
          <div>
            <label className="op-label block mb-1.5">Cargo description</label>
            <input data-testid="new-job-cargo" enterKeyHint="next" inputMode="text" className={field} value={form.cargo_description} onChange={(e) => set("cargo_description", e.target.value)} placeholder="4 Pallets Hotel Maintenance Supplies" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="op-label block mb-1.5">Owner</label>
              <Select value={form.assigned_owner} onValueChange={(v) => set("assigned_owner", v)}>
                <SelectTrigger data-testid="new-job-owner" className="h-9 border-line rounded-sm"><SelectValue placeholder="Assign" /></SelectTrigger>
                <SelectContent>{ownerOptions.map((n) => <SelectItem key={n} value={n}>{n === user?.name ? `${n} (me)` : n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="op-label block mb-1.5">Stage</label>
              <Select value={form.current_stage} onValueChange={(v) => set("current_stage", v)}>
                <SelectTrigger className="h-9 border-line rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="op-label block mb-1.5">Next action</label>
            <input data-testid="new-job-next-action" className={field} value={form.next_action} onChange={(e) => set("next_action", e.target.value)} placeholder="Confirm pickup window with carrier" />
          </div>
          {maritime && (
          <div className="border border-line rounded-sm">
            <button type="button" data-testid="new-job-finalmile-toggle" onClick={() => setShowFinalMile((s) => !s)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-left">
              <span className="op-label">Final-Mile &amp; Property Access <span className="text-ink-3 normal-case tracking-normal font-normal">(optional)</span></span>
              <ChevronDown className={`h-4 w-4 text-ink-3 transition-transform ${showFinalMile ? "rotate-180" : ""}`} />
            </button>
            {showFinalMile && (
              <div className="px-3 pb-3.5 space-y-3.5 border-t border-line pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="op-label block mb-1.5">Destination property</label>
                    <input data-testid="new-job-property" className={field} value={form.destination_property} onChange={(e) => set("destination_property", e.target.value)} placeholder="Blue Waters Resort" />
                  </div>
                  <div>
                    <label className="op-label block mb-1.5">Destination type</label>
                    <Select value={form.destination_type || undefined} onValueChange={(v) => set("destination_type", v)}>
                      <SelectTrigger data-testid="new-job-desttype" className="h-9 border-line rounded-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>{DEST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="op-label block mb-1.5">Access instructions</label>
                  <textarea data-testid="new-job-access" rows={2} value={form.access_instructions} onChange={(e) => set("access_instructions", e.target.value)}
                    className="w-full px-3 py-2 rounded-sm border border-line bg-surface text-sm focus:outline-none focus:ring-1 focus:ring-brand resize-none" placeholder="Use northern delivery gate; guard has manifest copy." />
                </div>
                <div>
                  <label className="op-label block mb-1.5">End recipient / owner</label>
                  <input data-testid="new-job-end-recipient" className={field} value={form.end_recipient_owner} onChange={(e) => set("end_recipient_owner", e.target.value)} placeholder="e.g., Smith Estate (Villa #12), Warehouse Dock B, Main Pavilion Kitchen" />
                </div>
                <div>
                  <label className="op-label block mb-1.5">Primary contact</label>
                  <div className="grid grid-cols-3 gap-3">
                    <input data-testid="new-job-primary-name" className={field} value={form.primary_contact_name} onChange={(e) => set("primary_contact_name", e.target.value)} placeholder="Name" />
                    <input data-testid="new-job-primary-title" className={field} value={form.primary_contact_title} onChange={(e) => set("primary_contact_title", e.target.value)} placeholder="Title / Role" />
                    <input data-testid="new-job-primary-phone" inputMode="tel" enterKeyHint="next" className={field} value={form.primary_contact_phone} onChange={(e) => set("primary_contact_phone", e.target.value)} placeholder="+1 268 555 1234" />
                  </div>
                </div>
                <div>
                  <label className="op-label block mb-1.5">Backup contact</label>
                  <div className="grid grid-cols-3 gap-3">
                    <input data-testid="new-job-backup-name" className={field} value={form.backup_contact_name} onChange={(e) => set("backup_contact_name", e.target.value)} placeholder="Name" />
                    <input data-testid="new-job-backup-title" className={field} value={form.backup_contact_title} onChange={(e) => set("backup_contact_title", e.target.value)} placeholder="Title / Role" />
                    <input data-testid="new-job-backup-phone" inputMode="tel" enterKeyHint="done" className={field} value={form.backup_contact_phone} onChange={(e) => set("backup_contact_phone", e.target.value)} placeholder="+1 268 555 5678" />
                  </div>
                </div>
              </div>
            )}
          </div>
          )}
          {maritime && (
            <div className="border border-line rounded-sm p-3 space-y-3.5" data-testid="new-job-maritime">
              <span className="op-label flex items-center gap-1.5"><Ship className="h-3.5 w-3.5" /> Maritime / Vessel Transit <span className="text-ink-3 normal-case tracking-normal font-normal">(optional)</span></span>
              <div>
                <label className="op-label block mb-1.5">Vessel name</label>
                <input data-testid="new-job-vessel" className={field} value={form.vessel_name} onChange={(e) => set("vessel_name", e.target.value)} placeholder="MV Island Trader" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="op-label block mb-1.5">Departure sailing time</label>
                  <input data-testid="new-job-sailing" type="datetime-local" className={field} value={form.departure_sailing_time} onChange={(e) => set("departure_sailing_time", e.target.value)} />
                </div>
                <div>
                  <label className="op-label block mb-1.5">Dock / Terminal slot</label>
                  <input data-testid="new-job-dock" className={field} value={form.dock_terminal_slot} onChange={(e) => set("dock_terminal_slot", e.target.value)} placeholder="Berth 4 · 06:00–08:00" />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="bg-white border-t-2 border-[#0B132B] p-4 shrink-0">
          <button type="submit" data-testid="new-job-submit" className="w-full bg-[#D97706] hover:bg-[#B45309] text-white font-black py-3 rounded-md border-2 border-[#0B132B] shadow-[2px_2px_0px_0px_#0B132B] active:translate-y-0.5 active:translate-x-0.5 active:shadow-none transition-all">
            Create Job
          </button>
        </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const QUICK = ["all", "active", "pickup-today", "delivery-today", "ready-to-close", "closeout-blocked", "closed"];

export default function ShipmentBoard() {
  const [jobs, setJobs] = useState([]);
  const [meta, setMeta] = useState({ stages: [], risk_statuses: [] });
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({ owner: "all", customer: "all", risk: "all", stage: "all" });
  const navigate = useNavigate();

  const quick = params.get("filter") || "all";
  const setQuick = (q) => {
    if (q === "all") params.delete("filter");
    else params.set("filter", q);
    setParams(params, { replace: true });
  };

  const load = () => client.get("/shipments").then((r) => setJobs(r.data));
  useEffect(() => {
    load();
    client.get("/meta").then((r) => setMeta(r.data));
    client.get("/customers").then((r) => setCustomers(r.data));
    client.get("/users").then((r) => setUsers(r.data));
  }, []);

  const move = async (job, stage) => {
    try {
      const r = await client.put(`/shipments/${job.id}`, { current_stage: stage });
      setJobs((prev) => prev.map((j) => (j.id === job.id ? r.data : j)));
      toast.success(stage === "Closed" ? "Job closed." : `${job.job_number} moved to ${stage}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not move job.");
      load();
    }
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const filtered = useMemo(() => {
    let list = jobs;
    if (quick !== "all" && BOARD_FILTERS[quick]) list = list.filter(BOARD_FILTERS[quick]);
    if (filters.owner !== "all") list = list.filter((j) => j.assigned_owner === filters.owner);
    if (filters.customer !== "all") list = list.filter((j) => j.customer_name === filters.customer);
    if (filters.risk !== "all") list = list.filter((j) => j.risk_status === filters.risk);
    if (filters.stage !== "all") list = list.filter((j) => j.current_stage === filters.stage);
    const q = search.trim().toLowerCase();
    if (q)
      list = list.filter((j) =>
        [j.job_number, j.customer_name, j.pickup_location, j.delivery_location, j.assigned_owner]
          .join(" ").toLowerCase().includes(q)
      );
    return list;
  }, [jobs, quick, filters, search]);

  const owners = useMemo(() => [...new Set(jobs.map((j) => j.assigned_owner))].sort(), [jobs]);
  const custNames = useMemo(() => [...new Set(jobs.map((j) => j.customer_name))].sort(), [jobs]);

  const tabCounts = useMemo(() => ({
    all: jobs.length,
    active: jobs.filter(BOARD_FILTERS.active).length,
    "pickup-today": jobs.filter(BOARD_FILTERS["pickup-today"]).length,
    "delivery-today": jobs.filter(BOARD_FILTERS["delivery-today"]).length,
    "ready-to-close": jobs.filter(BOARD_FILTERS["ready-to-close"]).length,
    "closeout-blocked": jobs.filter(BOARD_FILTERS["closeout-blocked"]).length,
    closed: jobs.filter(BOARD_FILTERS.closed).length,
  }), [jobs]);

  const byStage = (stage) => filtered.filter((j) => j.current_stage === stage);
  const anyFilter = quick !== "all" || filters.owner !== "all" || filters.customer !== "all" || filters.risk !== "all" || filters.stage !== "all";

  const FSelect = ({ label, k, options, testid }) => (
    <Select value={filters[k]} onValueChange={(v) => setF(k, v)}>
      <SelectTrigger data-testid={testid} className="h-8 border-line rounded-sm bg-surface text-[12.5px] w-auto min-w-[120px]">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{label}: All</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <Layout onSearch={setSearch}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <p className="op-label mb-1">Operations</p>
          <h1 className="font-heading text-2xl font-semibold text-ink">Dispatch Board</h1>
          <p className="text-sm text-ink-2 mt-1">{filtered.length} of {jobs.length} jobs shown.</p>
        </div>
        <NewJobDialog customers={customers} users={users} stages={meta.stages} maritime={!!meta.enable_interisland_transit} onCreated={load} />
      </div>

      {/* Quick filters + selects */}
      <div className="bg-surface border border-line rounded-md p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {QUICK.map((q) => (
            <button
              key={q}
              data-testid={`board-quick-${q}`}
              onClick={() => setQuick(q)}
              className={`h-8 px-3 rounded-sm text-[12.5px] font-medium border transition-colors inline-flex items-center gap-1.5 ${
                quick === q ? "bg-brand text-white border-brand" : "bg-surface text-ink-2 border-line hover:border-steel"
              }`}
            >
              <span>{q === "all" ? "All Jobs" : BOARD_FILTER_LABELS[q]}</span>
              <span
                data-testid={`board-quick-count-${q}`}
                className={`font-mono-data text-[10.5px] leading-none px-1.5 py-0.5 rounded-sm ${
                  quick === q ? "bg-white/20 text-white" : "bg-surface-alt text-ink-3 border border-line"
                }`}
              >
                {tabCounts[q]}
              </span>
            </button>
          ))}
        </div>
        <div className="h-6 w-px bg-line mx-1 hidden sm:block" />
        <div className="flex flex-wrap gap-1.5">
          <FSelect label="Stage" k="stage" options={meta.stages} testid="board-filter-stage" />
          <FSelect label="Owner" k="owner" options={owners} testid="board-filter-owner" />
          <FSelect label="Customer" k="customer" options={custNames} testid="board-filter-customer" />
          <FSelect label="Risk" k="risk" options={meta.risk_statuses} testid="board-filter-risk" />
        </div>
        {anyFilter && (
          <button
            data-testid="board-clear-filters"
            onClick={() => { setQuick("all"); setFilters({ owner: "all", customer: "all", risk: "all", stage: "all" }); }}
            className="h-8 px-2.5 rounded-sm text-[12.5px] text-ink-2 hover:text-danger flex items-center gap-1 transition-colors ml-auto"
          >
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        )}
      </div>

      {quick === "all" || quick === "active" ? (
        <div className="flex gap-3 overflow-x-auto pb-4 pr-4 mv-scroll">
          {(quick === "active" ? meta.stages.filter((s) => s !== "Closed") : meta.stages).map((stage) => {
            const list = byStage(stage);
            return (
              <div key={stage} className="w-[270px] shrink-0">
                <div className="flex items-center justify-between mb-2.5 px-0.5">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STAGE_META[stage] }} />
                    <span className="text-[13px] font-bold text-ink">{stage}</span>
                  </div>
                  <span data-testid={`board-col-count-${stage.replace(/\s+/g, "-").toLowerCase()}`} className="font-bold bg-slate-200 text-slate-800 text-xs px-2 py-0.5 rounded-full">{list.length}</span>
                </div>
                <div className="space-y-2.5 min-h-[60px]">
                  {list.map((j) => (
                    <JobCard key={j.id} job={j} stages={meta.stages} onMove={move} onOpen={(job) => navigate(`/app/shipments/${job.id}`)} />
                  ))}
                  {list.length === 0 && (
                    <div className="text-[12px] text-ink-3 border border-dashed border-line rounded-sm py-6 text-center">No jobs</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="board-empty" className="bg-surface border border-dashed border-line rounded-md py-16 text-center text-sm text-ink-3">
          No jobs match the {BOARD_FILTER_LABELS[quick]} filter.
        </div>
      ) : (
        <div data-testid="board-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 pb-4">
          {filtered.map((j) => (
            <JobCard key={j.id} job={j} stages={meta.stages} onMove={move} onOpen={(job) => navigate(`/app/shipments/${job.id}`)} />
          ))}
        </div>
      )}
    </Layout>
  );
}
